/**
 * Self-hosted implementation of AuthAdapter.
 * Issues and validates JWTs via a /auth REST endpoint backed by PostgreSQL.
 *
 * Tokens are stored in localStorage under the keys:
 *   nexus_access_token  — short-lived JWT
 *   nexus_refresh_token — long-lived refresh token
 */

import type { AuthAdapter, AuthUser, AuthSession, SignUpOptions } from '../interfaces';

const BASE = () =>
  process.env.NEXT_PUBLIC_SELF_HOSTED_API_URL ?? 'http://localhost:4000';

const STORAGE_ACCESS  = 'nexus_access_token';
const STORAGE_REFRESH = 'nexus_refresh_token';

type AuthStateCallback = (
  event: 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'USER_UPDATED',
  session: AuthSession | null
) => void;

const listeners: Set<AuthStateCallback> = new Set();

function notify(event: 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'USER_UPDATED', session: AuthSession | null) {
  listeners.forEach((cb) => cb(event, session));
}

function storeSession(session: AuthSession) {
  localStorage.setItem(STORAGE_ACCESS, session.accessToken);
  if (session.refreshToken) localStorage.setItem(STORAGE_REFRESH, session.refreshToken);
}

function clearSession() {
  localStorage.removeItem(STORAGE_ACCESS);
  localStorage.removeItem(STORAGE_REFRESH);
}

async function authFetch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

function decodeJwt(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}

function sessionFromTokens(accessToken: string, refreshToken: string | null): AuthSession {
  const claims = decodeJwt(accessToken);
  return {
    accessToken,
    refreshToken,
    expiresAt: typeof claims.exp === 'number' ? claims.exp : null,
    user: {
      id: String(claims.sub ?? ''),
      email: typeof claims.email === 'string' ? claims.email : null,
      emailConfirmedAt: typeof claims.email_confirmed_at === 'string' ? claims.email_confirmed_at : null,
      metadata: (claims.user_metadata as Record<string, unknown>) ?? {},
      createdAt: typeof claims.created_at === 'string' ? claims.created_at : new Date().toISOString(),
    },
  };
}

export class SelfHostedAuthAdapter implements AuthAdapter {
  async signUp(email: string, password: string, options?: SignUpOptions) {
    const res = await authFetch<{ access_token: string; refresh_token: string }>(
      '/auth/signup',
      { email, password, metadata: options?.metadata ?? {}, redirect_to: options?.redirectTo }
    );
    const session = sessionFromTokens(res.access_token, res.refresh_token);
    storeSession(session);
    notify('SIGNED_IN', session);
    return { user: session.user, session };
  }

  async signIn(email: string, password: string) {
    const res = await authFetch<{ access_token: string; refresh_token: string }>(
      '/auth/signin',
      { email, password }
    );
    const session = sessionFromTokens(res.access_token, res.refresh_token);
    storeSession(session);
    notify('SIGNED_IN', session);
    return { user: session.user, session };
  }

  async signOut() {
    clearSession();
    notify('SIGNED_OUT', null);
  }

  async getUser(): Promise<AuthUser | null> {
    const session = await this.getSession();
    return session?.user ?? null;
  }

  async getSession(): Promise<AuthSession | null> {
    if (typeof localStorage === 'undefined') return null;
    const accessToken = localStorage.getItem(STORAGE_ACCESS);
    if (!accessToken) return null;
    const refreshToken = localStorage.getItem(STORAGE_REFRESH);
    return sessionFromTokens(accessToken, refreshToken);
  }

  onAuthStateChange(callback: AuthStateCallback): () => void {
    listeners.add(callback);
    return () => listeners.delete(callback);
  }
}
